import React, { useEffect, useState, useRef } from 'react';
import Cookies from 'js-cookie';
import { Redirect } from 'react-router-dom';
import { getUserState } from '@carvana/core-auth';
import { datadogRum } from '@datadog/browser-rum';
import { CoreEvent, eventNames } from '@carvana/core-events';
import { useSnapshot } from 'valtio';
import { isCarvanaNativeApp } from '@carvana/core-native-messaging';
import {
  GarageInnerWrapper,
  GarageWrapper,
  HomeBodyWrapper,
  HomeContentWrapper,
  HomeWrapper,
  OneCarGarageWrapper,
} from './Home.styles';
import Garage from '../garage/Garage';
import { Blank } from '../blank/Blank';
import { getLandingPageState } from '../../store/GaragePageStore';
import { Spinner } from '../_common/spinner/Spinner';
import ComingSoonModal from '../_common/coming-soon-modal/ComingSoonModal';
import { AnalyticEvents, OneCarStatus } from '../../constants';
import { HeaderStore } from '../../store/header-store';
import { Analytics } from '../../services/analytics-service';

const Home = () => {
  const hasSentOpenAccountModalEvent = useRef(false);
  const { isLoggedIn } = getUserState();
  const addVehicleTestExpVal = Cookies.get('op-add-vehicle-test');
  const [landingPageState, setLandingPageState] = useState<any>();
  const [isLoading, setIsLoading] = useState(true);
  const [openComingSoonModal, setOpenComingSoonModal] = useState(false);
  const { headerLoaded } = useSnapshot(HeaderStore);

  useEffect(() => {
    const getData = async () => {
      const landingPageData = await getLandingPageState();
      setLandingPageState(landingPageData);
      setIsLoading(false);
    };
    getData();
  }, []);

  useEffect(() => {
    if (
      !isLoggedIn &&
      !landingPageState?.isGarageDataFetched &&
      !hasSentOpenAccountModalEvent.current &&
      headerLoaded &&
      !isCarvanaNativeApp()
    ) {
      // timeout is needed to give header enough time to load
      setTimeout(() => {
        hasSentOpenAccountModalEvent.current = true;
        CoreEvent.emit(eventNames.accountModal.open);
        Analytics.trackEvent(AnalyticEvents.openModalEvent);
        datadogRum.addAction(AnalyticEvents.openModalEvent);
      }, 1000);
    }
  }, [isLoggedIn, landingPageState?.isGarageDataFetched, headerLoaded]);


  useEffect(() => {
    if (landingPageState) {
      const { oneCarOnlyStatus, vehicleCount, isGarageDataFetched, garageDataError } = landingPageState;
      // Gate on isGarageDataFetched/!garageDataError so a failed load isn't logged as a real (empty) garage view.
      // The fetch only runs for logged-in users, so this also naturally excludes logged-out sessions.
      if (
        !isLoading &&
        isGarageDataFetched &&
        !garageDataError &&
        oneCarOnlyStatus !== OneCarStatus.POST_PURCHASE &&
        oneCarOnlyStatus !== OneCarStatus.APPRAISAL
      ) {
        Analytics.trackEvent(AnalyticEvents.viewMyCarvana, {
          vehicle_count: vehicleCount,
          isLoggedIn,
          is_impersonating: getUserState()?.impersonation?.is_impersonating,
        });
        datadogRum.addAction(AnalyticEvents.viewMyCarvana, {
          vehicle_count: vehicleCount,
          isLoggedIn,
          is_impersonating: getUserState()?.impersonation?.is_impersonating,
        });
      }
    }
  }, [landingPageState, isLoggedIn, isLoading]);

  const redirectToOvp = () => {
    const { ovpReferenceType, ovpVehicleReferenceId } = landingPageState;
    return `/my-carvana/vehicle/${ovpVehicleReferenceId}?referenceType=${ovpReferenceType}`;
  };

  if (isLoading) {
    return <Spinner customStyles={{ marginTop: '6%' }} />;
  }

  const { oneCarOnlyStatus, garageActiveState, garageDataError, vehicleCount } = landingPageState;

  return (
    <HomeWrapper>
      <HomeContentWrapper>
        <>
          {(oneCarOnlyStatus === OneCarStatus.PRE_PURCHASE || oneCarOnlyStatus === OneCarStatus.PRE_ORDER) && (
            <>
              <OneCarGarageWrapper>
                <GarageInnerWrapper>
                  <Garage landingPageState={landingPageState} />
                </GarageInnerWrapper>
              </OneCarGarageWrapper>
              <Blank caption="oneCar" />
            </>
          )}
          {(oneCarOnlyStatus === OneCarStatus.POST_PURCHASE || oneCarOnlyStatus === OneCarStatus.APPRAISAL) && (
            <>
              <Redirect to={redirectToOvp()} />
            </>
          )}
          {(oneCarOnlyStatus === '' ||
            oneCarOnlyStatus === OneCarStatus.POST_PURCHASE ||
            oneCarOnlyStatus === OneCarStatus.APPRAISAL) &&
            garageActiveState && (
              <HomeBodyWrapper>
                <GarageWrapper addVehicleExpValue={addVehicleTestExpVal}>
                  <Garage landingPageState={landingPageState} />
                </GarageWrapper>
              </HomeBodyWrapper>
            )}
          {!garageActiveState && !garageDataError && <Blank caption="blank" />}
          {/*  this will have left rail show error page */}
          {!garageActiveState && garageDataError && <Garage landingPageState={landingPageState} />}
        </>
      </HomeContentWrapper>
      <ComingSoonModal isOpen={openComingSoonModal} closeModal={() => setOpenComingSoonModal(false)} />
    </HomeWrapper>
  );
};

export default Home;
